// Hook utilitário para formatar dados de resumos PIS/COFINS
// Responsável por: gerar colunas, mapear linhas formatadas e fornecer util para atualizar valores editáveis
import { useCallback, useMemo, useState } from "react";

// Mapeia tipos para formatadores
const formatters = {
  cnpj: (value) => {
    if (!value) return "";
    const v = value.replace(/\D/g, "").padEnd(14, " ");
    return v.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, "$1.$2.$3/$4-$5");
  },
  number: (value) =>
    value === null || value === undefined ? "" : String(value),
  percent: (value) => {
    if (value === null || value === undefined || value === "") return "";
    const num = Number(value);
    if (Number.isNaN(num)) return value;
    return (
      new Intl.NumberFormat("pt-BR", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 4,
      }).format(num) + "%"
    );
  },
  date: (value) => {
    if (!value) return "";
    try {
      // aceita ISO ou yyyy-MM-dd
      const d = new Date(value);
      if (isNaN(d.getTime())) return value;
      return d.toLocaleDateString("pt-BR");
    } catch {
      return value;
    }
  },
  monetary: (value) => {
    if (value === null || value === undefined || value === "") return "";
    const num = Number(value);
    if (Number.isNaN(num)) return value;
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(num);
  },
  string: (value) =>
    value === null || value === undefined ? "" : String(value),
  boolean: (value) => (value ? "Sim" : "Não"),
};

// Tipos editáveis (por enquanto apenas boolean para "Composição Projeto")
const EDITABLE_TYPES = ["boolean"]; // pode ser expandido futuramente

export function useFormatResumoPisCofins(resumoRaw) {
  // Nova estrutura da API: dados e colunas diretos no resumo
  const colunas = resumoRaw?.colunas || {}; // { Label: tipo }
  const dadosOriginais = resumoRaw?.dados || [];

  // Estado local dos dados editáveis (mantém shape chave original => valor bruto)
  const [linhas, setLinhas] = useState(dadosOriginais);

  // Geração das definições de colunas para TableCardv2
  const columns = useMemo(() => {
    const normalize = (s) =>
      (s || "")
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .toLowerCase();

    const isProjeto = (label) =>
      normalize(label).includes("composicao projeto");
    const isCalculo = (label) =>
      normalize(label).includes("composicao calculo");

    const entries = Object.entries(colunas);
    const normal = entries.filter(
      ([label]) => !isProjeto(label) && !isCalculo(label)
    );
    const calculo = entries.filter(([label]) => isCalculo(label));
    const projeto = entries.filter(([label]) => isProjeto(label));

    const ordered = [...normal, ...calculo, ...projeto];

    return ordered.map(([label, tipo]) => ({
      key: label, // usaremos o label como chave externa
      label,
      tipo,
      editable: isProjeto(label) && EDITABLE_TYPES.includes(tipo),
    }));
  }, [colunas]);

  // Função para obter valor bruto de uma linha dado label + tipo (convenção: "Label__tipo")
  const getRawValue = useCallback(
    (row, label, tipo) => row[`${label}__${tipo}`],
    []
  );

  // Linhas formatadas para exibição na tabela
  const formattedRows = useMemo(() => {
    return linhas.map((row, idx) => {
      const base = { id: idx + 1, __raw: row, __index: idx };
      columns.forEach(({ label, tipo }) => {
        const raw = getRawValue(row, label, tipo);
        const formatter = formatters[tipo] || ((v) => v);
        base[label] = formatter(raw);
      });
      return base;
    });
  }, [linhas, columns, getRawValue]);

  // Atualiza valor editável (ex: boolean) preservando convenção de chave
  const updateCell = useCallback(
    (rowIndex, label, novoValor) => {
      setLinhas((prev) =>
        prev.map((linha, idx) => {
          if (idx !== rowIndex) return linha;
          const tipo = colunas[label];
          const chave = `${label}__${tipo}`;
          return {
            ...linha,
            [chave]: tipo === "boolean" ? !!novoValor : novoValor,
          };
        })
      );
    },
    [colunas]
  );

  // Monta payload para API (mantém shape original esperado: { dados: [...], colunas })
  const buildPayload = useCallback(
    () => ({
      dados: linhas,
      colunas,
    }),
    [linhas, colunas]
  );

  return {
    columns,
    formattedRows,
    updateCell,
    buildPayload,
    linhasOriginais: linhas,
  };
}

export default useFormatResumoPisCofins;

